import {i18n} from './applicant/i18n';
import * as validator from './applicant/validator';

export type Control =
  | HTMLButtonElement
  | HTMLFieldSetElement
  | HTMLInputElement
  | HTMLObjectElement
  | HTMLOutputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

type Handle = (
  this: Controller,
  control: Control,
  matcher: RegExpMatchArray,
  event: Event
) => void;

export class Listener {
  constructor(
    readonly type: string,
    readonly handle: Handle,
    readonly pattern: RegExp
  ) {}
}

export abstract class Controller {
  abstract readonly listeners: readonly Listener[];
  abstract readonly pattern?: RegExp;
  abstract onready(control: Control, matcher: RegExpMatchArray): Promise<void>;
  checkValidity(_control: Control, _matcher: RegExpMatchArray) {
    return true;
  }
}

export abstract class Form {
  static changed = false;
  static readonly registry: {
    [name: string]: new (form: HTMLFormElement) => Form;
  } = {};
  static register(name: string, ctor: new (form: HTMLFormElement) => Form) {
    this.registry[name] = ctor;
  }
  static start() {
    for (const form of Array.from(document.forms)) {
      const ctor = Form.registry[form.name];
      if (ctor)
        new ctor(form);
    }
  }
  static getControls(form: HTMLFormElement) {
    return Array.from(form.elements) as Control[];
  }
  static showLoadingModal() {
    const dialog = document.createElement('dialog');
    dialog.classList.add('fixed', 'loading');
    document.body.append(dialog);
    dialog.showModal();
    document.documentElement.style.cursor = 'wait';
    return dialog;
  }
  static async loading<T>(callback: () => T): Promise<T> {
    const cursor = document.documentElement.style.cursor;
    const dialog = this.showLoadingModal();
    try {
      return await callback();
    } finally {
      dialog.close();
      dialog.remove();
      document.documentElement.style.cursor = cursor;
    }
  }
  protected readonly controllers: readonly Controller[];
  constructor(protected readonly form: HTMLFormElement,
              controllers: readonly Controller[] = [],
              protected readonly search = false) {
    this.controllers = [
      ...controllers.slice(0),
      new AsciiController,
      new DateController,
      new MonthController,
      // new EnglishTextAreaController];
    ];
    for (const tuple of this.getTuples()) {
      const [controller, control, matcher] = tuple;
      controller.onready(control, matcher);
    }
    const params: {
      [type: string]: {
        controller: Controller;
        listener: Listener;
      }[];
    } = {};
    for (const controller of this.controllers)
      controller.listeners.forEach((listener) => {
        params[listener.type] ||= [];
        params[listener.type]!.push({ controller, listener });
      });
    for (const type in params)
      form.addEventListener(type, event => {
        for (let target = event.target as HTMLElement;
             form.contains(target) && target != form;
             target = target.parentElement!) {
          const control = target as Control;
          if (!control.name)
            continue;
          for (const param of params[type]!) {
            const matcher = control.name.match(param.listener.pattern);
            if (matcher)
              param.listener.handle.call(
                param.controller, control, matcher, event);
          }
        }
      });
    for (const type of ['focus', 'mouseenter'])
      form.addEventListener(type, event => {
        const control = event.target as Control;
        if (type == 'mouseenter' && control == document.activeElement)
          return;
        if (control.classList.contains('invalid-input'))
          control.reportValidity();
      }, true);
    form.addEventListener('change', () => {
      if (!this.search)
        Form.changed = true;
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (await this.validate(event))
        this.onsubmit(event);
      else
        this.onerror();
    });
  }
  protected async validate(_event?: SubmitEvent) {
    const controls = Form.getControls(this.form);
    for (const control of controls) {
      control.classList.remove('invalid-input');
      control.setCustomValidity('');
    }
    return this.checkControlsValidity(controls) &&
      this.checkControllersValidity() &&
      this.checkValidity();
  }
  private checkControlsValidity(controls: Control[]) {
    const invalids = controls.filter(control => {
      return control.willValidate && !control.validity.valid;
    });
    for (const invalid of invalids) {
      invalid.classList.add('invalid-input');
      invalid.setCustomValidity(this.validationMessage(invalid));
    }
    return invalids.length == 0;
  }
  private validationMessage(control: Control) {
    const validity = control.validity;
    switch (control.type) {
      case 'text':
        if (validity.valueMissing)
          return i18n('text.valueMissing');
        break;
      case 'radio':
        if (validity.valueMissing)
          return i18n('radio.valueMissing');
        break;
      case 'checkbox':
        if (validity.valueMissing)
          return i18n('checkbox.valueMissing');
        break;
      case 'select-one':
        if (validity.valueMissing)
          return i18n('select-one.valueMissing');
        break;
      }
    return control.validationMessage;
  }
  private checkControllersValidity() {
    const tuples = this.getTuples();
    const invalids = tuples.filter(tuple => {
      const [controller, control, matcher] = tuple;
      return !controller.checkValidity(control, matcher);
    });
    return invalids.length == 0;
  }
  protected getTuples() {
    const tuples: [Controller, Control, RegExpMatchArray][] = [];
    const controls = Form.getControls(this.form);
    for (const controller of this.controllers) {
      const pattern = controller.pattern;
      if (!pattern)
        continue;
      for (const control of controls) {
        if (!control.willValidate)
          continue;
        const matcher = control.name.match(pattern)
        if (!matcher)
          continue;
        tuples.push([controller, control, matcher]);
      }
    }
    return tuples;
  }
  protected checkValidity() {
    return true;
  }
  protected onsubmit(_event: SubmitEvent) {
    Form.changed = false;
    this.form.submit();
  }
  protected onerror() {
    const invalid = Form.getControls(this.form).find(control => {
      return control.willValidate && !control.validity.valid;
    });
    if (!invalid)
      return;
    invalid.scrollIntoView({block: 'center'});
    invalid.focus();
    if ('select' in invalid)
      invalid.select();
  }
  protected async loading<T>(callback: () => T) {
    const dialog = Form.showLoadingModal();
    const value = await callback();
    document.documentElement.style.cursor = '';
    dialog.close();
    dialog.remove();
    return value;
  }
}

export abstract class Preview {
  private display;
  protected abstract readonly pattern: RegExp;
  constructor(protected readonly form: HTMLFormElement) {
    this.display = this.form.style.display;
  }
  show() {
    for (const control of Form.getControls(this.form)) {
      if (control.type != 'select-one')
        continue;
      const select = control as HTMLSelectElement;
      const selectedIndex = select.selectedIndex;
      for (const option of Array.from(select.options))
        option.defaultSelected = false;
      select.options[selectedIndex]!.defaultSelected = true;
    }
    const form = this.form.cloneNode(true) as HTMLFormElement;
    this.form.style.display = 'none';
    this.form.after(form);
    this.previewForm(form);
    form.addEventListener('submit', event => {
      this.onsubmit(event);
    });
  }
  protected previewForm(form: HTMLFormElement) {
    for (const c of Form.getControls(form)) {
      if (!this.pattern.test(c.name))
        continue;
      switch (c.type) {
        case 'hidden':
          break;
        case 'text': case 'email': case 'date': case 'month': case 'number':
          const input = c as HTMLInputElement;
          this.preview(input, element => {
            element.textContent = input.value;
          });
          break;
        case 'radio':
          const radio = c as HTMLInputElement;
          if (!radio.checked)
            break;
          this.preview(radio, element => {
            element.textContent = radio.parentNode!.textContent;
          });
          break;
        case 'textarea':
          const textarea = c as HTMLTextAreaElement;
          this.preview(textarea, element => {
            element.textContent = textarea.value;
            element.style.whiteSpace = 'pre';
          });
          break;
        case 'select-one':
          const select = c as HTMLSelectElement;
          this.preview(select, element => {
            element.textContent = select.options[select.selectedIndex]!.text;
          });
          break;
        default:
          console.log(c);
      }
    }
  }
  hide() {
    if (this.form.style.display != 'none')
      return;
    this.form.style.display = this.display;
    this.form.nextSibling!.remove();
  }
  protected preview(control: Control,
                    callback: (element: HTMLElement) => void) {
    const element = control.closest('.input, .multi-language-item-input');
    if (control.form!.contains(element))
      callback(element as HTMLElement);
  }
  protected onsubmit(event: Event) {
    event.preventDefault();
    Form.changed = false;
    this.form.submit();
    Form.showLoadingModal();
  }
}

class AsciiController extends Controller {
  readonly pattern = undefined;
  readonly listeners = [
    new Listener('change', this.onchange, /.*/),
    new Listener('compositionend', this.onchange, /.*/),
    new Listener('paste', this.onpaste, /.*/),
  ];
  async onready(_control: Control, _matcher: RegExpMatchArray) {
  }
  private onchange(control: Control, _matcher: RegExpMatchArray) {
    if (!control.hasAttribute('data-ascii'))
      return;
    if (control.type == 'text') {
      const text = control as HTMLInputElement;
      text.value = this.toHalfWidth(text.value).replace(/[^ -~]*/g, '');
    } else if (control.type == 'textarea') {
      const textarea = control as HTMLTextAreaElement;
      textarea.value =
        this.toHalfWidth(textarea.value).replace(/[^\x00-\x7F]*/g, '');
    }
  }
  private onpaste(control: Control, matcher: RegExpMatchArray) {
    setTimeout(() => {
      this.onchange(control, matcher);
    });
  }
  private toHalfWidth(value: string) {
    return value.replace(/[！-～]/g, wide => {
      const half = wide.charCodeAt(0) - '！'.charCodeAt(0) + '!'.charCodeAt(0);
      return String.fromCharCode(half);
    });
  }
  // checkValidity(control: Control, _matcher: RegExpMatchArray) {
  //   if (!control.hasAttribute('data-ascii'))
  //     return true;
  //   if (control.type == 'text') {
  //     const text = control as HTMLInputElement;
  //     if (text.value == text.value.replace(/[^ -~]*/g, ''))
  //       return true;
  //   } else if (control.type == 'textarea') {
  //     const textarea = control as HTMLTextAreaElement;
  //     if (textarea.value == textarea.value.replace(/[^\x00-\x7F]*/g, ''))
  //       return true;
  //   }
  //   control.classList.add('invalid-input');
  //   control.setCustomValidity('半角英数字で入力してください');
  //   return false;
  // }
}

class DateController extends Controller {
  readonly pattern = /.*/;
  readonly listeners = [
    new Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (!input)
      return;
    input.placeholder = 'YYYY-MM-DD';
    input.pattern = '\\d{4}-\\d{2}-\\d{2}';
  }
  private onchange(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (!input)
      return;
    input.value = input.value.replace(/[！-～]/g, wide => {
      const half = wide.charCodeAt(0) - '！'.charCodeAt(0) + '!'.charCodeAt(0);
      return String.fromCharCode(half);
    });
  }
  override checkValidity(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (input && input.willValidate && input.value) {
      const date = validator.TextDateValidator.parse(input.value);
      if (date)
        return true;
      input.classList.add('invalid-input');
      input.setCustomValidity('不正な日付です');
      return false;
    }
    return true;
  }
  private target(control: Control) {
    if (control.getAttribute('type') == 'date' && control.type == 'text')
      return control as HTMLInputElement;
    return;
  }
}

class MonthController extends Controller {
  readonly pattern = /.*/;
  readonly listeners = [
    new Listener('change', this.onchange, this.pattern),
  ];
  async onready(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (!input)
      return;
    input.placeholder = 'YYYY-MM';
    input.pattern = '\\d{4}-\\d{2}';
  }
  private onchange(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (!input)
      return;
    input.value = input.value.replace(/[！-～]/g, wide => {
      const half = wide.charCodeAt(0) - '！'.charCodeAt(0) + '!'.charCodeAt(0);
      return String.fromCharCode(half);
    });
  }
  override checkValidity(control: Control, _matcher: RegExpMatchArray) {
    const input = this.target(control);
    if (input && input.willValidate && input.value) {
      const date = validator.TextMonthValidator.parse(input.value);
      if (date)
        return true;
      input.classList.add('invalid-input');
      input.setCustomValidity('不正な日付です');
      return false;
    }
    return true;
  }
  private target(control: Control) {
    if (control.getAttribute('type') == 'month' && control.type == 'text')
      return control as HTMLInputElement;
    return;
  }
}
/*
class EnglishTextAreaController extends Controller {
  readonly pattern = /_en\]$/;
  readonly listeners = [];
  async onready(_control: Control, _matcher: RegExpMatchArray) {
  }
  checkValidity(control: Control, _matcher: RegExpMatchArray) {
    if (control.type != 'textarea')
      return true;
    const textarea = control as HTMLTextAreaElement;
    if (/^[\x00-\x7F]*$/.test(textarea.value))
      return true;
    textarea.classList.add('invalid-input');
    textarea.setCustomValidity('半角英数字で入力してください');
    return false;
  }
}
*/
window.addEventListener('load', () => {
  Form.start();
  document.querySelector('.content')?.classList.add('ready');
});

window.addEventListener('beforeunload', event => {
  if (Form.changed && document.documentElement)
    event.preventDefault();
});
